// client/src/services/fuelService.ts

import { apiClient } from './api';

// Types pour la gestion du carburant
export interface FuelRecord {
  id: string;
  vehicle_id: string;
  driver_id: string | null;
  tenant_id: string;
  fuel_type: string;
  quantity: number;
  price_per_unit: number;
  total_cost: number;
  mileage: number;
  location?: string;
  station_name?: string;
  receipt_number?: string;
  receipt_url?: string;
  consumption?: number;
  distance_since_last_refuel?: number;
  notes?: string;
  is_full_tank: boolean;
  recorded_at: string;
  created_by?: string;
  created_at: string;
  updated_at: string;

  // Enriched data from joins
  license_plate?: string;
  brand?: string;
  model?: string;
  driver_license?: string;
  driver_first_name?: string;
  driver_last_name?: string;
}

export interface CreateFuelRecordData {
  vehicle_id: string;
  driver_id?: string;
  fuel_type: 'diesel' | 'essence' | 'sp95' | 'sp98' | 'gpl' | 'electrique' | 'hybride';
  quantity: number;
  price_per_unit: number;
  mileage: number;
  location?: string;
  station_name?: string;
  receipt_number?: string;
  notes?: string;
  is_full_tank?: boolean;
  recorded_at?: string;
}

export interface UpdateFuelRecordData {
  fuel_type?: string;
  quantity?: number;
  price_per_unit?: number;
  mileage?: number;
  location?: string;
  station_name?: string;
  receipt_number?: string;
  notes?: string;
  is_full_tank?: boolean;
  recorded_at?: string;
}

export interface FuelRecordFilters {
  page?: number;
  limit?: number;
  vehicle_id?: string;
  driver_id?: string;
  fuel_type?: string;
  start_date?: string;
  end_date?: string;
}

export interface FuelStatistics {
  global: {
    total_cost: string;
    total_quantity: string;
    avg_consumption: string;
    vehicles_count: number;
    alerts_count: number;
  };
  by_vehicle: VehicleFuelStats[];
  alerts: FuelAlert[];
}

export interface VehicleFuelStats {
  vehicle_id: string;
  license_plate: string;
  brand: string;
  model: string;
  vehicle_type: string;
  tenant_id: string;
  total_refuels: number;
  total_fuel_cost: number;
  total_fuel_quantity: number;
  avg_consumption: number;
  best_consumption: number;
  worst_consumption: number;
  total_distance_tracked: number;
  avg_price_per_unit: number;
  avg_refuel_cost: number;
  last_refuel_date: string;
  first_refuel_date: string;
  current_year_cost: number;
  current_year_refuels: number;
}

export interface FuelAlert {
  vehicle_id: string;
  license_plate: string;
  alert_type: string;
  alert_message: string;
  current_consumption: number;
  avg_consumption: number;
}

export interface VehicleDetailedFuelStats {
  vehicle: {
    id: string;
    license_plate: string;
    brand: string;
    model: string;
  };
  statistics: {
    total_refuels: number;
    total_fuel_cost: number;
    total_fuel_quantity: number;
    avg_consumption: number;
  };
  efficiency_trend: EfficiencyTrendPoint[];
  next_refuel_estimate: {
    days_until_refuel: number;
    estimated_date: string;
    avg_days_between_refuels: number;
  } | null;
}

export interface EfficiencyTrendPoint {
  month: string;
  avg_consumption: number;
  total_cost: number;
  total_distance: number;
  refuel_count: number;
}

export interface FuelSummary {
  period: {
    start: string;
    end: string;
  };
  monthly_summary: MonthlySummary[];
}

export interface MonthlySummary {
  tenant_id: string;
  vehicle_id: string;
  month: string;
  refuel_count: number;
  total_cost: number;
  total_quantity: number;
  avg_consumption: number;
  total_distance: number;
  first_refuel: string;
  last_refuel: string;
}

/**
 * Service pour la gestion du carburant
 */
export class FuelService {
  private readonly baseUrl = '/admin/fuel';

  /**
   * Récupérer la liste des enregistrements de carburant
   */
  async getFuelRecords(filters: FuelRecordFilters = {}): Promise<{
    records: FuelRecord[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const url = apiClient.buildUrl(this.baseUrl, filters);
    return apiClient.get<{
      records: FuelRecord[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(url);
  }

  /**
   * Récupérer un enregistrement de carburant par ID
   */
  async getFuelRecordById(id: string): Promise<FuelRecord> {
    return apiClient.get<FuelRecord>(`${this.baseUrl}/${id}`);
  }

  /**
   * Créer un nouvel enregistrement de carburant
   */
  async createFuelRecord(data: CreateFuelRecordData): Promise<{
    message: string;
    record: FuelRecord;
    timestamp: string;
  }> {
    return apiClient.post<{
      message: string;
      record: FuelRecord;
      timestamp: string;
    }>(this.baseUrl, data);
  }

  /**
   * Mettre à jour un enregistrement de carburant
   */
  async updateFuelRecord(id: string, data: UpdateFuelRecordData): Promise<{
    message: string;
    record: FuelRecord;
    timestamp: string;
  }> {
    return apiClient.put<{
      message: string;
      record: FuelRecord;
      timestamp: string;
    }>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Supprimer un enregistrement de carburant
   */
  async deleteFuelRecord(id: string): Promise<{
    message: string;
    id: string;
    timestamp: string;
  }> {
    return apiClient.delete<{
      message: string;
      id: string;
      timestamp: string;
    }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Récupérer les statistiques globales de carburant
   */
  async getFuelStatistics(): Promise<FuelStatistics> {
    return apiClient.get<FuelStatistics>(`${this.baseUrl}/stats`);
  }

  /**
   * Récupérer les statistiques de carburant pour un véhicule
   */
  async getVehicleFuelStats(vehicleId: string): Promise<VehicleDetailedFuelStats> {
    return apiClient.get<VehicleDetailedFuelStats>(`${this.baseUrl}/vehicles/${vehicleId}/stats`);
  }

  /**
   * Récupérer le résumé de carburant pour une période
   */
  async getFuelSummary(startDate?: string, endDate?: string): Promise<FuelSummary> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/summary`, params);
    return apiClient.get<FuelSummary>(url);
  }

  /**
   * Upload un reçu pour un enregistrement
   */
  async uploadReceipt(id: string, file: File): Promise<{
    message: string;
    receipt_url: string;
    timestamp: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post<{
      message: string;
      receipt_url: string;
      timestamp: string;
    }>(`${this.baseUrl}/${id}/receipt`, formData);
  }
}

// Instance singleton du service
export const fuelService = new FuelService();
